import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Mail, MapPin, Clock, Send, CheckCircle, Shield, ChevronDown, ChevronUp } from 'lucide-react';
import { contactAPI } from '@/lib/api';
import { toast } from 'sonner';
import { SEO } from '@/components/SEO';

const serviceOptions = [
  { value: 'company-reset', label: 'Company Reset' },
  { value: 'accounting', label: 'Accounting & Bookkeeping' },
  { value: 'tax-compliance', label: 'Tax & Compliance' },
  { value: 'registration', label: 'Registration Services' },
  { value: 'micro-cfo', label: 'Micro CFO' },
  { value: 'cxo-hiring', label: 'CXO Hiring' },
  { value: 'other', label: 'Other' },
];

const faqs = [
  {
    question: 'What are your typical fees?',
    answer: 'Fees depend on scope; most micro clients start with simple monthly retainers or fixed-fee projects after an initial discussion.',
  },
  {
    question: 'Do you work outside Hyderabad/Mumbai?',
    answer: 'Yes. We work with clients across India using online tools for meetings and document sharing.',
  },
  {
    question: 'How quickly can you start?',
    answer: 'For most micro clients, we can start within 5–7 working days after our first consultation and document review.',
  },
  {
    question: 'Do you work with startups?',
    answer: 'Yes. We help early-stage startups set up accounting systems, manage compliance, and hire their first finance leader when they are ready to scale.',
  },
];

export default function ContactPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service_interest: '',
    message: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleServiceChange = (value) => {
    setFormData({ ...formData, service_interest: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await contactAPI.submit(formData);
      setIsSubmitted(true);
      toast.success('Thank you! We\'ll be in touch soon.');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="pt-20 min-h-screen bg-[#F9F9F7]" data-testid="contact-page">
        <div className="py-20 md:py-28">
          <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
            <div className="w-16 h-16 rounded-full bg-[#059669]/10 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-[#059669]" />
            </div>
            <h1 className="font-secondary text-3xl font-bold text-slate-900 mb-4">Thank You!</h1>
            <p className="text-lg text-slate-600 mb-8">
              We've received your message and will get back to you within 24 hours.
            </p>
            <Button 
              onClick={() => { setIsSubmitted(false); setFormData({ name: '', email: '', phone: '', company: '', service_interest: '', message: '' }); }}
              className="bg-[#E84C3D] hover:bg-[#C0392B] text-white"
            >
              Send Another Message
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20" data-testid="contact-page">
      <SEO 
        title="Contact Us - Schedule a Free Consultation"
        path="/contact"
        description="Get in touch with Ratnatrraya Consulting for accounting, tax, Micro CFO, and CXO hiring services. Free consultation. Offices in Hyderabad and Mumbai, serving Pan-India."
        keywords="contact Ratnatrraya, free consultation, accounting services contact, Hyderabad CA, Mumbai CA, micro business help"
      />
      {/* Hero */}
      <section className="py-20 md:py-28 bg-[#F9F9F7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-sm font-medium tracking-widest text-[#E84C3D] uppercase mb-4 block">Contact Us</span>
              <h1 className="font-secondary text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                Let's Start a Conversation
              </h1>
              
              {/* ICP Line */}
              <div className="bg-white p-4 rounded-lg border border-slate-200 mb-6">
                <p className="text-sm text-slate-600">
                  <strong className="text-slate-900">Best suited for:</strong> Micro businesses under ₹10 Cr, NGOs, and firms hiring CFO/Tax/Legal leaders.
                </p>
              </div>

              <p className="text-slate-600 leading-relaxed mb-2">
                Whether you're looking for financial services for your business or seeking executive talent, 
                we're here to help. Reach out and let's discuss.
              </p>
              <p className="text-sm text-slate-500 mb-8">
                We typically respond within one business day.
              </p>

              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-[#E84C3D]/10 flex items-center justify-center shrink-0">
                    <Mail className="w-5 h-5 text-[#E84C3D]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-0.5 text-sm">Email</h4>
                    <p className="text-slate-600 text-sm">support@ratnatrraya.consulting</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-[#E84C3D]/10 flex items-center justify-center shrink-0">
                    <MapPin className="w-5 h-5 text-[#E84C3D]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-0.5 text-sm">Locations</h4>
                    <p className="text-slate-600 text-sm">Hyderabad, Mumbai, India</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-[#E84C3D]/10 flex items-center justify-center shrink-0">
                    <Clock className="w-5 h-5 text-[#E84C3D]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-0.5 text-sm">Business Hours</h4>
                    <p className="text-slate-600 text-sm">Mon - Fri: 9:00 AM - 6:00 PM IST</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Form Card */}
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <h2 className="font-secondary text-2xl font-semibold text-slate-900 mb-6">
                Send Us a Message
              </h2>

              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="name" className="text-sm">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      className="h-11 bg-slate-50 border-slate-200"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="email" className="text-sm">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      className="h-11 bg-slate-50 border-slate-200"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="phone" className="text-sm">Phone Number</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+91 98765 43210"
                      className="h-11 bg-slate-50 border-slate-200"
                      data-testid="contact-phone-input"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="company" className="text-sm">Company Name</Label>
                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Your company"
                      className="h-11 bg-slate-50 border-slate-200"
                      data-testid="contact-company-input"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="service_interest" className="text-sm">Type of Enquiry *</Label>
                  <Select value={formData.service_interest} onValueChange={handleServiceChange} required>
                    <SelectTrigger className="h-11 bg-slate-50 border-slate-200" data-testid="contact-service-select">
                      <SelectValue placeholder="Select enquiry type" />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">This helps us route your query to the right person.</p>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="message" className="text-sm">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your requirements..."
                    className="min-h-[100px] bg-slate-50 border-slate-200"
                    data-testid="contact-message-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-[#E84C3D] hover:bg-[#C0392B] text-white h-12"
                  disabled={isSubmitting}
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send size={16} />
                      Send Message
                    </span>
                  )}
                </Button>

                {/* Privacy Note */}
                <div className="flex items-start gap-2 text-xs text-slate-500">
                  <Shield className="w-4 h-4 shrink-0 mt-0.5" />
                  <p>We keep your information confidential and never share it with third parties.</p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <h2 className="font-secondary text-2xl md:text-3xl font-semibold text-slate-900 mb-2">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className="bg-[#F9F9F7] rounded-xl overflow-hidden"
                data-testid={`faq-${index}`}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left"
                >
                  <span className="font-medium text-slate-900">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-slate-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-500" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-5">
                    <p className="text-slate-600 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
